/**
 * Client-side auth for Vite. Communicates with the NestJS backend's
 * NextAuth endpoints via fetch (all /api/* requests are proxied to the
 * backend in dev; in production point VITE_API_BASE_URL to the backend).
 *
 * Cookie-based sessions work because:
 *  - In dev: Vite proxy forwards Cookie/Set-Cookie headers transparently
 *  - In prod: both apps sit on the same root domain (e.g. swiftmvp.com)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type SessionUser = {
  id: string;
  email: string | null;
  name: string | null;
  image: string | null;
  role: string | null;
  /** Set by NextAuth session callback - mirrors DB ADMIN role + ADMIN_EMAILS. */
  adminAccess?: boolean;
};

export type Session = {
  user: SessionUser;
  expires: string;
} | null;

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  session: Session;
  status: AuthStatus;
  /** Re-fetches the session from the backend. */
  refresh: () => Promise<void>;
  /** Triggers the NextAuth sign-out endpoint, then redirects. */
  signOut: (options?: { callbackUrl?: string }) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  session: null,
  status: "loading",
  refresh: async () => {},
  signOut: async () => {},
});

async function fetchSession(): Promise<Session> {
  try {
    const res = await fetch("/api/auth/session", {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Session;
    // NextAuth returns {} (empty object) when unauthenticated.
    if (!data || !("user" in data)) return null;
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = useCallback(async () => {
    const s = await fetchSession();
    setSession(s);
    setStatus(s ? "authenticated" : "unauthenticated");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(
    async (options?: { callbackUrl?: string }) => {
      try {
        // Get CSRF token first (required by NextAuth for POST sign-out).
        const csrfRes = await fetch("/api/auth/csrf", {
          credentials: "include",
        });
        const { csrfToken } = (await csrfRes.json()) as {
          csrfToken?: string;
        };

        await fetch("/api/auth/signout", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            csrfToken: csrfToken ?? "",
            callbackUrl: options?.callbackUrl ?? "/signin",
            json: "true",
          }),
        });
      } catch {
        // Ignore errors - session is invalidated server-side; redirect anyway.
      }
      setSession(null);
      setStatus("unauthenticated");
      window.location.href = options?.callbackUrl ?? "/signin";
    },
    [],
  );

  return (
    <AuthContext.Provider value={{ session, status, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Convenience hook - mirrors next-auth/react's useSession shape. */
export function useSession() {
  const { session, status } = useAuth();
  return { data: session, status };
}

export function isAdmin(session: Session): boolean {
  if (!session?.user) return false;
  if (session.user.adminAccess === true) return true;
  return session.user.role === "ADMIN";
}
